import {
  SystemStyleObject,
  defineStyle,
  defineStyleConfig,
} from '@chakra-ui/react';
import {
  getTypographyToken,
  ColorMode,
  getBoxSizingStyles,
  pxToRem,
} from '@allergan-data-labs/alle-elements-core';
import { ButtonColorScheme, ButtonVariants } from './button';

interface VariantAttributes {
  colorMode: ColorMode;
  baseBgColor: string;
  hoverFocusBgColor: string;
  activeBgColor: string;
  disabledBgColor: string;
  textColor: string;
  disabledTextColor: string;
  borderColor: string | null;
  disabledBorderColor?: string;
}

const createVariantStyles = ({
  colorMode,
  baseBgColor,
  hoverFocusBgColor,
  disabledBgColor,
  activeBgColor,
  textColor,
  disabledTextColor,
  borderColor,
  disabledBorderColor,
}: VariantAttributes) => {
  const styles: SystemStyleObject = {
    bg: baseBgColor,
    color: textColor,
    _hover: {
      ':not(.static)': {
        bg: hoverFocusBgColor,
        color: textColor,
        _disabled: {
          cursor: 'not-allowed',
          bg: disabledBgColor,
          color: disabledTextColor,
          boxShadow: 'none',
        },
      },
    },
    _focusVisible: {
      ':not(.static)': {
        outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
        bg: hoverFocusBgColor,
        color: textColor,
        outlineStyle: 'solid',
        outlineWidth: '4px',
      },
    },
    _active: {
      ':not(.static)': {
        outline: 'none',
        bg: activeBgColor,
      },
    },
    _disabled: {
      ':not(.static)': {
        opacity: 1,
        bg: disabledBgColor,
        color: disabledTextColor,
        boxShadow: 'none',
      },
    },
    '.static': {
      _disabled: {
        bg: baseBgColor,
        color: textColor,
        borderColor: borderColor ? borderColor : undefined,
        cursor: 'default',
      },
      _hover: {
        bg: baseBgColor,
        color: textColor,
      },
      _active: {
        bg: baseBgColor,
        color: textColor,
      },
      _focusVisible: {
        outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
        bg: baseBgColor,
        color: textColor,
        outlineStyle: 'solid',
        outlineWidth: '4px',
      },
    },
  };

  if (borderColor) {
    styles.borderWidth = '1px';
    styles.borderStyle = 'solid';
    styles.borderColor = borderColor;
    styles._disabled.borderWidth = '1px';
    styles._disabled.borderStyle = 'solid';
    styles._disabled.borderColor = disabledBorderColor;
  }

  return defineStyle(styles);
};

const baseStyle = () => {
  return defineStyle({
    ...getBoxSizingStyles(),
    position: 'relative',
    cursor: 'pointer',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...getTypographyToken('Body/Medium/Regular'),
    borderRadius: 'Small',
    whiteSpace: 'nowrap',
  });
};

type VariantNames =
  | 'ghostAction'
  | 'ghostBrand'
  | 'ghostNegative'
  | 'ghostPositive'
  | 'outlineAction'
  | 'outlineBrand'
  | 'outlineMuted'
  | 'outlineNegative'
  | 'outlinePositive'
  | 'solidAction'
  | 'solidBrand'
  | 'solidMuted'
  | 'solidNegative'
  | 'solidPositive'
  | 'tintedAction'
  | 'tintedBrand'
  | 'tintedMuted'
  | 'tintedNegative'
  | 'tintedPositive';

type VariantColorSchemes =
  | 'Action'
  | 'Brand'
  | 'Muted'
  | 'Positive'
  | 'Negative';

export const getVariantName = (
  variant: ButtonVariants,
  colorScheme: ButtonColorScheme
): VariantNames => {
  const variantColorScheme: VariantColorSchemes = (colorScheme
    .charAt(0)
    .toUpperCase() + colorScheme.slice(1).toLowerCase()) as VariantColorSchemes;

  if (colorScheme === 'muted') {
    if (variant === 'ghost') {
      return 'ghostAction';
    }
  }

  return `${variant}${variantColorScheme}` as VariantNames;
};

export const getVariantStyles = (
  variant: ButtonVariants,
  colorScheme: ButtonColorScheme,
  colorMode: ColorMode
) => {
  const tintedVariant = createVariantStyles({
    colorMode,
    textColor: `light.Text/Neutral/Default`,
    borderColor: null,
    baseBgColor: `${colorMode}.Container/Neutral/Tinted interactive/Enable`,
    hoverFocusBgColor: `${colorMode}.Container/Neutral/Tinted interactive/Hover`,
    activeBgColor: `${colorMode}.Container/Neutral/Tinted interactive/Pressed`,
    disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Default`,
    disabledTextColor: `light.Text/Neutral/Disabled/Default`,
  });

  const variantName = getVariantName(variant, colorScheme);
  const variantMap: { [key in VariantNames]: SystemStyleObject } = {
    ghostAction: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    ghostBrand: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Brand/Default`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Brand/Transparent interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Brand/Transparent interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    ghostNegative: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Status/Error medium 3`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Transparent interactive/Hover negative`,
      activeBgColor: `${colorMode}.Container/Status/Transparent interactive/Pressed negative`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    ghostPositive: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Status/Positive medium 3`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      activeBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      disabledBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      disabledTextColor: `${colorMode}.Text/Status/Positive medium 3`,
    }),
    outlineAction: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
      borderColor: `${colorMode}.Border/Neutral/Default`,
      baseBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
      disabledBorderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
    }),
    outlineBrand: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Brand/Default`,
      borderColor: `${colorMode}.Border/Brand/Medium 3`,
      baseBgColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Brand/Transparent interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Brand/Transparent interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
      disabledBorderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
    }),
    outlineMuted: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
      borderColor: `${colorMode}.Border/Neutral/Subtle 2`,
      baseBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
      hoverFocusBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Mute hover`,
      activeBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Mute pressed`,
      disabledBgColor: `${colorMode}.Container/Transparent/Clear`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
      disabledBorderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
    }),
    outlineNegative: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Status/Error medium 3`,
      borderColor: `${colorMode}.Border/Status/Error medium 3`,
      baseBgColor: `${colorMode}.Container/Neutral/Transparent interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Transparent interactive/Hover negative`,
      activeBgColor: `${colorMode}.Container/Status/Transparent interactive/Pressed negative`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Transparent`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
      disabledBorderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
    }),
    outlinePositive: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Status/Positive medium 3`,
      borderColor: `${colorMode}.Border/Status/Positive medium 3`,
      baseBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      activeBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      disabledBgColor: `${colorMode}.Container/Status/Transparent interactive/Enable positive`,
      disabledTextColor: `${colorMode}.Text/Status/Positive medium 3`,
      disabledBorderColor: `${colorMode}.Border/Status/Positive medium 3`,
    }),
    solidAction: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Inverted`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Neutral/Interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Neutral/Interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Neutral/Interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Default`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    solidBrand: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Inverted`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Brand/Interactive/Enable`,
      hoverFocusBgColor: `${colorMode}.Container/Brand/Interactive/Hover`,
      activeBgColor: `${colorMode}.Container/Brand/Interactive/Pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Default`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    solidMuted: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Neutral/Interactive/Mute enable`,
      hoverFocusBgColor: `${colorMode}.Container/Neutral/Interactive/Mute hover`,
      activeBgColor: `${colorMode}.Container/Neutral/Interactive/Mute pressed`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Default`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    solidNegative: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Inverted`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Status/Interactive/Enable negative`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Interactive/Hover negative`,
      activeBgColor: `${colorMode}.Container/Status/Interactive/Pressed negative`,
      disabledBgColor: `${colorMode}.Container/Neutral/Disabled/Default`,
      disabledTextColor: `${colorMode}.Text/Neutral/Disabled/Default`,
    }),
    solidPositive: createVariantStyles({
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Inverted`,
      borderColor: null,
      baseBgColor: `${colorMode}.Container/Status/Positive medium 3`,
      hoverFocusBgColor: `${colorMode}.Container/Status/Positive medium 3`,
      activeBgColor: `${colorMode}.Container/Status/Positive medium 3`,
      disabledBgColor: `${colorMode}.Container/Status/Positive medium 3`,
      disabledTextColor: `${colorMode}.Text/Neutral/Inverted`,
    }),
    tintedAction: tintedVariant,
    tintedBrand: tintedVariant,
    tintedMuted: tintedVariant,
    tintedNegative: tintedVariant,
    tintedPositive: tintedVariant,
  };

  return variantMap[variantName] || variantMap.solidAction;
};

const sizes = {
  xs: defineStyle({
    ...getTypographyToken('Body/Small/Regular'),
    px: '.5rem',
    py: pxToRem('6px'),
    maxHeight: '32px',
    height: '32px',
    minWidth: '32px',
  }),
  sm: defineStyle({
    ...getTypographyToken('Body/Small/Regular'),
    px: '.75rem',
    py: '.84375rem',
    maxHeight: '48px',
    height: '48px',
    minWidth: '48px',
  }),
  md: defineStyle({
    ...getTypographyToken('Body/Medium/Regular'),
    px: '1rem',
    py: '1.03125rem',
    maxHeight: '56px',
    height: '56px',
    minWidth: '56px',
  }),
  lg: defineStyle({
    ...getTypographyToken('Body/Large/Medium'),
    px: '1.625rem',
    py: '1.40625rem',
    maxHeight: '72px',
    height: '72px',
    minWidth: '72px',
  }),
};

export const ButtonTheme = defineStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    size: 'md',
  },
});
